import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import Button from './ui/Button';
import FormField from './ui/FormField';
import { validate, required, email, minLength, matches } from '../utils/validation';
import PasswordInput from './PasswordInput';
import '../App.css';
import { translateError } from '../utils/errorMessages';

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, tenant, isAuthenticated, hasPermission } = useAuth();

  const [name, setName] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      if (hasPermission('members:write') || hasPermission('events:write')) {
        navigate('/admin/calendar');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, hasPermission, navigate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const nameError = validate(name, [
      required(t('validation.nameRequired')),
      minLength(2, t('validation.nameMinLength'))
    ]);
    if (nameError) newErrors.name = nameError;

    const emailError = validate(emailValue, [
      required(t('validation.emailRequired')),
      email(t('validation.emailInvalid'))
    ]);
    if (emailError) newErrors.email = emailError;

    const passwordError = validate(password, [
      required(t('validation.passwordRequired')),
      minLength(8, t('validation.passwordMinLength'))
    ]);
    if (passwordError) newErrors.password = passwordError;

    const confirmPasswordError = validate(confirmPassword, [
      required(t('validation.confirmPasswordRequired')),
      matches(password, t('common.password'), t('validation.passwordsMustMatch'))
    ]);
    if (confirmPasswordError) newErrors.confirmPassword = confirmPasswordError;

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    if (!privacyConsent) {
      setError(t('gdpr.consentRequired'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.auth.register({ name, email: emailValue, password, phoneNumber: phoneNumber || undefined });

      // Check if email verification is required
      if ((response.data as any)?.requiresVerification) {
        setRegistrationSuccess(true);
      } else {
        // Existing verified user adding membership - use normal register flow
        await register({ name, email: emailValue, password, phoneNumber: phoneNumber || undefined });
        // Navigation will happen via useEffect above
      }
    } catch (err: any) {
      const message = translateError(err, t, 'auth.registerFailed');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Show message if registration is disabled for this organization
  if (tenant.slug && !tenant.isLoading && !tenant.name) {
    return (
      <div className="page-content">
        <div className="form-container">
          <h2>{t('auth.signUp')}</h2>
          <div className="error-message">
            {t('auth.orgNotFound')}
          </div>
          <Link to="/login" className="form-link">
            {t('nav.login')}
          </Link>
        </div>
      </div>
    );
  }

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <div className="page-content">
        <div className="form-container">
          {tenant.name && (
            <div className="organization-badge">
              {tenant.name}
            </div>
          )}

          <h2>{t('auth.signUp')}</h2>

          <div className="success-message" role="status">
            {t('auth.registrationSuccess')}
          </div>

          <Link to="/login" className="form-link">
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="form-container">
        {/* Show organization name if on tenant subdomain */}
        {tenant.name && (
          <div className="organization-badge">
            {tenant.name}
          </div>
        )}

        <h2>{t('auth.signUp')}</h2>

        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={submitHandler}>
          <FormField
            htmlFor="name"
            label={t('auth.name')}
            error={fieldErrors.name}
            required
          >
            <input
              type="text"
              id="name"
              placeholder={t('auth.name')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: '' }));
              }}
              disabled={isLoading}
            />
          </FormField>
          <FormField
            htmlFor="email"
            label={t('common.email')}
            error={fieldErrors.email}
            required
          >
            <input
              type="email"
              id="email"
              placeholder={t('auth.enterEmail')}
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value);
                if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
              }}
              disabled={isLoading}
            />
          </FormField>
          <FormField
            htmlFor="phoneNumber"
            label={t('common.phoneNumber')}
          >
            <input
              type="tel"
              id="phoneNumber"
              placeholder={t('users.enterPhoneNumber')}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={isLoading}
            />
          </FormField>
          <FormField
            htmlFor="password"
            label={t('common.password')}
            error={fieldErrors.password}
            required
          >
            <PasswordInput
              id="password"
              placeholder={t('auth.enterPassword')}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: '' }));
              }}
              disabled={isLoading}
            />
          </FormField>
          <FormField
            htmlFor="confirmPassword"
            label={t('auth.confirmPassword')}
            error={fieldErrors.confirmPassword}
            required
          >
            <PasswordInput
              id="confirmPassword"
              placeholder={t('auth.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (fieldErrors.confirmPassword) setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
              }}
              disabled={isLoading}
            />
          </FormField>
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={privacyConsent}
                onChange={(e) => setPrivacyConsent(e.target.checked)}
                disabled={isLoading}
                required
              />
              <span>
                {t('gdpr.consentLabel')}{' '}
                <Link to="/privacy-policy" target="_blank">{t('cookies.learnMore')}</Link>
              </span>
            </label>
          </div>
          <Button type="submit" fullWidth loading={isLoading}>
            {t('auth.signUp')}
          </Button>
        </form>
        <Link to="/login" className="form-link">
          {t('auth.haveAccount')} {t('nav.login')}
        </Link>
      </div>
    </div>
  );
};

export default RegisterPage;
